"use client";

import { getTossSdkMessage } from "@/lib/toss-payment-guidance";
import { captureTossDiagnostic } from "@/lib/toss-callback-diagnostics";
import { getTossProductProfile, isTossProductProfileConfigured } from "@/lib/toss-product";
import type { TossCheckout } from "@/lib/payment-provider";

type WidgetHandle = { destroy: () => Promise<void> };
export interface TossWidgets {
  setAmount: (amount: { currency: "KRW"; value: number }) => Promise<void>;
  renderPaymentMethods: (options: { selector: string; variantKey?: string }) => Promise<WidgetHandle>;
  renderAgreement: (options: { selector: string; variantKey?: string }) => Promise<WidgetHandle>;
  requestPayment: (options: Record<string, unknown>) => Promise<void>;
}
type TossSdk = (clientKey: string) => { widgets: (options: { customerKey: string }) => TossWidgets };
declare global { interface Window { TossPayments?: TossSdk; } }

let sdkPromise: Promise<TossSdk> | null = null;
function loadTossSdk(): Promise<TossSdk> {
  if (window.TossPayments) return Promise.resolve(window.TossPayments);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<TossSdk>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://js.tosspayments.com/v2/standard";
    script.async = true;
    const timer = window.setTimeout(fail, 15000);
    function fail() {
      window.clearTimeout(timer);
      script.remove();
      reject(new Error("결제 수단을 불러오지 못했습니다. 다시 시도해주세요."));
    }
    script.onload = () => {
      window.clearTimeout(timer);
      if (window.TossPayments) resolve(window.TossPayments);
      else fail();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  }).catch((error: unknown) => { sdkPromise = null; throw error; });
  return sdkPromise;
}

export interface TossCheckoutController {
  ready: Promise<void>;
  requestPayment: (checkout: TossCheckout) => Promise<void>;
  destroy: () => Promise<void>;
}
// SDK는 페이지당 한 쌍만 허용한다. 모달 재열기/StrictMode의 이전 비동기
// 렌더 및 destroy가 끝난 뒤 다음 렌더를 실행한다.
let lifecycle: Promise<unknown> = Promise.resolve();
let activeOwner: symbol | null = null;
export function createTossCheckout(options: {
  clientKey: string; customerKey: string; amount: number; productId: string; theme?: "light" | "dark";
  paymentMethodsSelector: string; agreementSelector: string;
}): TossCheckoutController {
  const owner = Symbol("toss-checkout");
  let disposed = false;
  let requesting = false;
  let profile: ReturnType<typeof getTossProductProfile>;
  let widgets: TossWidgets | undefined;
  let methods: WidgetHandle | undefined;
  let agreement: WidgetHandle | undefined;
  const cleanup = async () => {
    await Promise.all([methods?.destroy(), agreement?.destroy()]);
    methods = undefined;
    agreement = undefined;
    if (activeOwner === owner) activeOwner = null;
  };
  const ready = lifecycle.then(async () => {
    if (disposed) return;
    if (!/^(test|live)_gck_/.test(options.clientKey)) throw new Error("결제 설정을 확인 중입니다. 잠시 후 다시 시도해주세요.");
    profile = getTossProductProfile(options.productId);
    if (!isTossProductProfileConfigured(profile)) throw new Error("상품별 결제 UI 설정을 확인해주세요.");
    const toss = await loadTossSdk();
    if (disposed) return;
    if (activeOwner) throw new Error("열려 있는 다른 구매 화면을 닫고 다시 시도해주세요.");
    widgets = toss(options.clientKey).widgets({ customerKey: options.customerKey });
    await widgets.setAmount({ currency: "KRW", value: options.amount });
    if (disposed) return;
    try {
      activeOwner = owner;
      methods = await widgets.renderPaymentMethods({
        selector: options.paymentMethodsSelector,
        variantKey: options.theme === "dark" && profile.paymentDarkVariantKey ? profile.paymentDarkVariantKey : profile.paymentVariantKey,
      });
      if (disposed) { await cleanup(); return; }
      const agreementVariantKey = process.env.NEXT_PUBLIC_TOSS_AGREEMENT_VARIANT_KEY?.trim();
      agreement = await widgets.renderAgreement({
        selector: options.agreementSelector,
        ...(agreementVariantKey ? { variantKey: agreementVariantKey } : {}),
      });
      if (disposed) await cleanup();
    } catch (error) { await cleanup(); throw error; }
  });
  lifecycle = ready.catch(() => {});
  return {
    ready,
    async requestPayment(checkout) {
      await ready;
      if (disposed || !widgets || !methods || !agreement || requesting) throw new Error("결제 준비 상태를 확인해주세요.");
      if (checkout.clientKey !== options.clientKey || checkout.customerKey !== options.customerKey || checkout.amount !== options.amount || checkout.paymentVariantKey !== profile.paymentVariantKey || (options.theme === "dark" && profile.paymentDarkVariantKey && checkout.paymentDarkVariantKey !== profile.paymentDarkVariantKey) || checkout.isCulturalExpenses !== profile.isCulturalExpenses) {
        throw new Error("결제 정보가 변경되었습니다. 페이지를 새로고침해주세요.");
      }
      requesting = true;
      const successUrl = new URL("/api/toss/confirm", window.location.origin);
      const failUrl = new URL("/api/toss/fail", window.location.origin);
      successUrl.searchParams.set("token", checkout.callbackToken);
      // 인증·승인에는 사용하지 않는 진단 전용 주문 연결 ID.
      successUrl.searchParams.set("diagnosticOrderId", checkout.orderId);
      failUrl.searchParams.set("orderId", checkout.orderId);
      failUrl.searchParams.set("token", checkout.callbackToken);
      try {
        // 서버에서 검증한 금액으로 최종 동기화한다. 결제수단과 현금영수증 입력은
        // 주문서 UI가 관리하며, 필수 약관 미동의도 SDK가 결제 요청 전에 검증한다.
        await widgets.setAmount({ currency: "KRW", value: checkout.amount });
        if (disposed || !methods || !agreement) throw new Error("구매 화면이 닫혔습니다. 다시 시도해주세요.");
        captureTossDiagnostic("before-sdk-request", {
          diagnosticOrderId: checkout.orderId, amount: checkout.amount, successPath: successUrl.pathname,
        });
        await widgets.requestPayment({
          transfer: { isCulturalExpenses: profile.isCulturalExpenses },
          virtualAccount: { isCulturalExpenses: profile.isCulturalExpenses },
          orderId: checkout.orderId, orderName: checkout.orderName,
          customerEmail: checkout.customerEmail, customerName: checkout.customerName,
          successUrl: successUrl.toString(), failUrl: failUrl.toString(),
        });
      } catch (error) {
        if (error && typeof error === "object" && "code" in error && typeof error.code === "string") {
          throw new Error(getTossSdkMessage(error.code));
        }
        throw error;
      } finally { requesting = false; }
    },
    destroy() {
      disposed = true;
      const done = lifecycle.then(cleanup);
      lifecycle = done.catch(() => {});
      return done;
    },
  };
}
